import React, { useState } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { Spinner, Funnel, Table, Clock, Check, X, CheckCircle, Pencil, Trash, FileText } from '@phosphor-icons/react';

export const TimelineTab = ({ timeline, xlsxInputRef, xlsxUploading, uploadXlsx, caseId, loadAll }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', actor: '', risk: '', verified: '', source: '' });
  const [filteredTimeline, setFilteredTimeline] = useState(null);
  const [filtering, setFiltering] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const applyFilters = async () => {
    const hasFilter = Object.values(filters).some(v => v);
    if (!hasFilter) { setFilteredTimeline(null); return; }
    setFiltering(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get(`/cases/${caseId}/timeline/filtered?${params.toString()}`);
      setFilteredTimeline(data.timeline);
    } catch (err) { logger.error('Timeline filter failed:', err); }
    finally { setFiltering(false); }
  };

  const clearFilters = () => { setFilters({ date_from: '', date_to: '', actor: '', risk: '', verified: '', source: '' }); setFilteredTimeline(null); };

  const openDocPreview = async (docName) => {
    if (!docName) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/documents/by-name/${encodeURIComponent(docName)}`);
      if (data.document) {
        const docId = data.document._id;
        const { data: preview } = await api.get(`/documents/${docId}/preview`);
        setPreviewDoc(preview);
      }
    } catch {
      setPreviewDoc({ file_name: docName, extracted_text: 'Unable to load document preview.', file_type: '' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const saveEdit = async (entryId) => {
    try { await api.put(`/timeline/${entryId}`, editData); setEditingId(null); setEditData({}); await loadAll(); } catch { alert('Failed to save'); }
  };
  const toggleVerify = async (entryId, current) => {
    try { await api.put(`/timeline/${entryId}`, { verified: !current }); await loadAll(); } catch (err) { logger.error('Toggle verify failed:', err); }
  };
  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this timeline entry?')) return;
    try { await api.delete(`/timeline/${entryId}`); await loadAll(); } catch { alert('Failed to delete'); }
  };

  const displayTimeline = filteredTimeline !== null ? filteredTimeline : timeline;

  return (
    <div data-testid="timeline-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-outfit text-lg font-semibold text-[#09090B]">
          {displayTimeline.length} Timeline Entries{filteredTimeline !== null ? ' (filtered)' : ''}
        </h3>
        <div className="flex gap-2">
          <button data-testid="toggle-filters-button" onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 border text-sm font-ibm rounded-md transition-all ${showFilters ? 'border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5' : 'border-[#E5E7EB] text-[#09090B] hover:bg-[#F3F4F6]'}`}>
            <Funnel size={14} /> Filters
          </button>
          <label data-testid="upload-xlsx-button"
            className={`flex items-center gap-2 px-3 py-2 border border-[#E5E7EB] text-[#09090B] font-ibm text-sm rounded-md hover:bg-[#F3F4F6] cursor-pointer transition-all ${xlsxUploading ? 'opacity-50' : ''}`}>
            {xlsxUploading ? <><Spinner size={14} className="animate-spin" /> Importing...</> : <><Table size={14} /> Import XLSX</>}
            <input ref={xlsxInputRef} type="file" className="hidden" onChange={uploadXlsx} accept=".xlsx,.xls" disabled={xlsxUploading} />
          </label>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-4" data-testid="timeline-filters">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
            <div><label className="block text-xs font-bold uppercase text-[#6B7280] font-ibm mb-1">From</label>
              <input type="date" value={filters.date_from} onChange={e => setFilters({...filters, date_from: e.target.value})} className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-sm font-ibm" /></div>
            <div><label className="block text-xs font-bold uppercase text-[#6B7280] font-ibm mb-1">To</label>
              <input type="date" value={filters.date_to} onChange={e => setFilters({...filters, date_to: e.target.value})} className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-sm font-ibm" /></div>
            <div><label className="block text-xs font-bold uppercase text-[#6B7280] font-ibm mb-1">Actor</label>
              <input type="text" placeholder="Name..." value={filters.actor} onChange={e => setFilters({...filters, actor: e.target.value})} className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-sm font-ibm" /></div>
            <div><label className="block text-xs font-bold uppercase text-[#6B7280] font-ibm mb-1">Risk</label>
              <select value={filters.risk} onChange={e => setFilters({...filters, risk: e.target.value})} className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-sm font-ibm">
                <option value="">All</option><option value="any">Has Risk</option></select></div>
            <div><label className="block text-xs font-bold uppercase text-[#6B7280] font-ibm mb-1">Verified</label>
              <select value={filters.verified} onChange={e => setFilters({...filters, verified: e.target.value})} className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-sm font-ibm">
                <option value="">All</option><option value="true">Verified</option><option value="false">Unverified</option></select></div>
            <div><label className="block text-xs font-bold uppercase text-[#6B7280] font-ibm mb-1">Source</label>
              <input type="text" placeholder="Doc..." value={filters.source} onChange={e => setFilters({...filters, source: e.target.value})} className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-sm font-ibm" /></div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={clearFilters} className="px-3 py-1.5 text-xs border border-[#E5E7EB] text-[#6B7280] font-ibm rounded hover:bg-[#F3F4F6]">Clear</button>
            <button onClick={applyFilters} disabled={filtering} className="px-3 py-1.5 text-xs bg-[#002FA7] text-white font-ibm rounded hover:bg-[#00237D] disabled:opacity-50">
              {filtering ? 'Filtering...' : 'Apply'}
            </button>
          </div>
        </div>
      )}

      {displayTimeline.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-[#E5E7EB] rounded-lg">
          <Clock size={48} className="mx-auto text-[#E5E7EB] mb-3" />
          <p className="text-[#6B7280] font-ibm">{filteredTimeline !== null ? 'No entries match filters.' : 'No timeline entries yet. Analyze documents or import XLSX.'}</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-x-auto">
          <table className="w-full" data-testid="timeline-table">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <th className="text-left px-3 py-3 text-xs font-bold tracking-[0.1em] uppercase text-[#6B7280] font-ibm whitespace-nowrap">Date</th>
                <th className="text-left px-3 py-3 text-xs font-bold tracking-[0.1em] uppercase text-[#6B7280] font-ibm whitespace-nowrap">Time</th>
                <th className="text-left px-3 py-3 text-xs font-bold tracking-[0.1em] uppercase text-[#6B7280] font-ibm whitespace-nowrap">Medium</th>
                <th className="text-left px-3 py-3 text-xs font-bold tracking-[0.1em] uppercase text-[#6B7280] font-ibm whitespace-nowrap">Who &rarr; Whom</th>
                <th className="text-left px-3 py-3 text-xs font-bold tracking-[0.1em] uppercase text-[#6B7280] font-ibm" style={{minWidth:'300px'}}>Summary</th>
                <th className="text-left px-3 py-3 text-xs font-bold tracking-[0.1em] uppercase text-[#6B7280] font-ibm" style={{minWidth:'150px'}}>Remark</th>
                <th className="text-left px-3 py-3 text-xs font-bold tracking-[0.1em] uppercase text-[#6B7280] font-ibm whitespace-nowrap">Received</th>
                <th className="text-left px-3 py-3 text-xs font-bold tracking-[0.1em] uppercase text-[#6B7280] font-ibm whitespace-nowrap">Document</th>
                <th className="text-center px-2 py-3 text-xs font-bold tracking-[0.1em] uppercase text-[#6B7280] font-ibm w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayTimeline.map((t, i) => {
                const hasRisk = t.risk_flags?.length > 0;
                const isEditing = editingId === t._id;
                const whoToWhom = t.who_said_to_whom || (t.from_actor && t.to_actor ? `${t.from_actor} to ${t.to_actor}` : t.actors || '-');
                return (
                  <tr key={t._id || `row-${i}`} className={`border-b border-[#E5E7EB] last:border-0 hover:bg-[#F9FAFB]/50 transition-colors ${hasRisk ? 'bg-red-50/30' : ''} ${t.verified ? 'bg-green-50/20' : ''}`} data-testid={`timeline-row-${i}`}>
                    {isEditing ? (
                      <>
                        <td className="px-3 py-2"><input value={editData.date_iso||''} onChange={e=>setEditData({...editData,date_iso:e.target.value})} className="w-24 px-1.5 py-1 border border-[#E5E7EB] rounded text-xs font-ibm" /></td>
                        <td className="px-3 py-2"><input value={editData.time_display||''} onChange={e=>setEditData({...editData,time_display:e.target.value})} className="w-20 px-1.5 py-1 border border-[#E5E7EB] rounded text-xs font-ibm" /></td>
                        <td className="px-3 py-2"><input value={editData.medium||''} onChange={e=>setEditData({...editData,medium:e.target.value})} className="w-20 px-1.5 py-1 border border-[#E5E7EB] rounded text-xs font-ibm" /></td>
                        <td className="px-3 py-2"><input value={editData.who_said_to_whom||''} onChange={e=>setEditData({...editData,who_said_to_whom:e.target.value})} className="w-32 px-1.5 py-1 border border-[#E5E7EB] rounded text-xs font-ibm" /></td>
                        <td className="px-3 py-2"><textarea value={editData.ai_summary||''} onChange={e=>setEditData({...editData,ai_summary:e.target.value})} rows={2} className="w-full px-1.5 py-1 border border-[#E5E7EB] rounded text-xs font-ibm resize-none" /></td>
                        <td className="px-3 py-2"><textarea value={editData.ai_remark||''} onChange={e=>setEditData({...editData,ai_remark:e.target.value})} rows={2} className="w-full px-1.5 py-1 border border-[#E5E7EB] rounded text-xs font-ibm resize-none" /></td>
                        <td className="px-3 py-2 text-xs text-[#6B7280]">{t.date_received || '-'}</td>
                        <td className="px-3 py-2 text-xs text-[#6B7280]">{t.associated_doc_names}</td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex items-center gap-1 justify-center">
                            <button onClick={()=>saveEdit(t._id)} className="p-1 bg-[#002FA7] text-white rounded text-xs"><Check size={12}/></button>
                            <button onClick={()=>setEditingId(null)} className="p-1 border border-[#E5E7EB] rounded text-xs text-[#6B7280]"><X size={12}/></button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-3 text-sm font-ibm font-semibold text-[#002FA7] whitespace-nowrap">{t.date_iso}</td>
                        <td className="px-3 py-3 text-xs font-ibm text-[#6B7280] whitespace-nowrap">{t.time_display || '-'}</td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="text-xs font-ibm font-semibold px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#09090B]">{t.medium || t.location_medium || '-'}</span>
                        </td>
                        <td className="px-3 py-3 text-xs font-ibm text-[#09090B] max-w-[180px]">
                          <span className="line-clamp-2" title={whoToWhom}>{whoToWhom}</span>
                        </td>
                        <td className="px-3 py-3 text-sm font-ibm text-[#09090B]">
                          <p className="line-clamp-3">{t.ai_summary}</p>
                          {hasRisk && <div className="flex gap-1 mt-1 flex-wrap">{t.risk_flags.map((f,j)=><span key={`flag-${j}`} className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-700 rounded font-ibm">{f.replace(/_/g,' ')}</span>)}</div>}
                          {t.verified && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-green-50 text-green-700 rounded font-ibm mt-1"><CheckCircle size={10}/> Verified</span>}
                        </td>
                        <td className="px-3 py-3 text-xs font-ibm text-[#6B7280] italic">{t.ai_remark || '-'}</td>
                        <td className="px-3 py-3 text-xs font-ibm text-[#6B7280] whitespace-nowrap">{t.date_received || '-'}</td>
                        <td className="px-3 py-3">
                          {t.associated_doc_names ? (
                            <button onClick={() => openDocPreview(t.associated_doc_names)} data-testid={`preview-doc-${i}`}
                              className="text-xs text-[#002FA7] font-ibm font-semibold hover:underline cursor-pointer text-left max-w-[130px] truncate block" title={t.associated_doc_names}>
                              {t.associated_doc_names}
                            </button>
                          ) : <span className="text-xs text-[#6B7280]">-</span>}
                        </td>
                        <td className="px-2 py-3 text-center">
                          <div className="flex items-center gap-0.5 justify-center">
                            <button onClick={()=>toggleVerify(t._id,t.verified)} title={t.verified?'Unverify':'Verify'} className={`p-1 rounded hover:bg-[#F3F4F6] ${t.verified?'text-green-600':'text-[#6B7280]'}`}><CheckCircle size={14} weight={t.verified?'fill':'regular'}/></button>
                            <button onClick={()=>{setEditingId(t._id);setEditData({date_iso:t.date_iso,time_display:t.time_display||'',medium:t.medium||t.location_medium||'',who_said_to_whom:t.who_said_to_whom||'',actors:t.actors||'',ai_summary:t.ai_summary||'',ai_remark:t.ai_remark||''});}} className="p-1 rounded hover:bg-[#F3F4F6] text-[#6B7280]"><Pencil size={14}/></button>
                            <button onClick={()=>deleteEntry(t._id)} className="p-1 rounded hover:bg-red-50 text-[#6B7280] hover:text-red-600"><Trash size={14}/></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Document Preview Modal */}
      {(previewDoc || previewLoading) && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => { setPreviewDoc(null); }} data-testid="doc-preview-modal">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB]">
              <div className="flex items-center gap-2">
                <FileText size={20} className="text-[#002FA7]" />
                <span className="font-outfit font-semibold text-[#09090B]">{previewDoc?.file_name || 'Loading...'}</span>
                {previewDoc?.file_type && <span className="text-xs px-1.5 py-0.5 bg-[#F3F4F6] text-[#6B7280] rounded font-ibm">{previewDoc.file_type.toUpperCase()}</span>}
              </div>
              <button onClick={() => setPreviewDoc(null)} className="p-1 hover:bg-[#F3F4F6] rounded"><X size={18} className="text-[#6B7280]" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {previewLoading ? (
                <div className="text-center py-12"><Spinner size={24} className="animate-spin text-[#002FA7] mx-auto" /><p className="text-sm text-[#6B7280] font-ibm mt-2">Loading document...</p></div>
              ) : (
                <pre className="text-sm font-ibm text-[#09090B] whitespace-pre-wrap leading-relaxed">{previewDoc?.extracted_text || 'No text content available.'}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
